import * as T from 'three'
import { X_GUARDRAIL } from '../../../../../config/blissGuardrails'
import type { Bed } from './beds'
import { bunkJoinery } from './bunk-joinery'

type Point = [number, number]

/** Clip end cuts to the frame so diagonal tips cannot poke through it. */
function clip(points: Point[], a: number, b: number, c: number): Point[] {
  const result: Point[] = []
  for (let i = 0; i < points.length; i++) {
    const p = points[i],
      q = points[(i + 1) % points.length]
    const dp = a * p[0] + b * p[1] - c,
      dq = a * q[0] + b * q[1] - c
    if (dp <= 1e-8) result.push(p)
    if ((dp < 0 && dq > 0) || (dp > 0 && dq < 0)) {
      const ratio = dp / (dp - dq)
      result.push([p[0] + ratio * (q[0] - p[0]), p[1] + ratio * (q[1] - p[1])])
    }
  }
  return result
}

/** One continuous diagonal; opposing pieces are cut to meet it without intersection. */
export function xBayPolygons(width: number, height: number, stock: number): Point[][] {
  const rect: Point[] = [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ]
  const length = Math.hypot(width, height),
    nx = -height / length,
    ny = width / length
  const rising = clip(clip(rect, nx, ny, stock / 2), -nx, -ny, stock / 2)
  const offset = (width * height) / length
  const falling = clip(clip(rect, -nx, ny, offset + stock / 2), nx, -ny, -offset + stock / 2)
  return [rising, clip(falling, nx, ny, -stock / 2), clip(falling, -nx, -ny, -stock / 2)].filter(
    p => p.length >= 3
  )
}

export function createXGuardrailPanel(
  width: number,
  name: string,
  height: number = X_GUARDRAIL.height
) {
  const group = new T.Group()
  group.name = name
  const { stock } = X_GUARDRAIL
  const openingHeight = height - 2 * stock
  function mesh(geometry: T.BufferGeometry, part: string, x = 0, y = 0) {
    const m = new T.Mesh(geometry, new T.MeshStandardMaterial({ color: 0xe2cea8, roughness: 0.72 }))
    m.name = `${name}-${part}`
    m.userData.finishPart = part === 'top' ? 'topRail' : 'accent'
    m.position.set(x / 12, y / 12, 0)
    m.add(
      new T.LineSegments(
        new T.EdgesGeometry(geometry),
        new T.LineBasicMaterial({ color: 0x9a8268, transparent: true, opacity: 0.22 })
      )
    )
    group.add(m)
  }
  function board(part: string, x: number, y: number, w: number, h: number) {
    const g = new T.BoxGeometry(w / 12, h / 12, stock / 12)
    g.translate(w / 24, h / 24, stock / 24)
    mesh(g, part, x, y)
  }
  if (width <= 2 * stock) {
    board('return', 0, 0, width, height)
    return group
  }
  board('bottom', 0, 0, width, stock)
  board('top', 0, height - stock, width, stock)
  const bays = Math.max(1, Math.round((width - stock) / 19.25))
  const step = (width - stock) / bays
  for (let i = 0; i <= bays; i++) board(`upright-${i}`, i * step, stock, stock, openingHeight)
  for (let i = 0; i < bays; i++) {
    for (const [j, points] of xBayPolygons(step - stock, openingHeight, stock).entries()) {
      const shape = new T.Shape(points.map(([x, y]) => new T.Vector2(x / 12, y / 12)))
      mesh(
        new T.ExtrudeGeometry(shape, { depth: stock / 12, bevelEnabled: false, steps: 1 }),
        `bay-${i}-diagonal-${j}`,
        i * step + stock,
        stock
      )
    }
  }
  return group
}

/** Three-sided stair wrap, or short foot returns framing the ladder's entry. */
export function createXGuardrails(b: Bed, base: number, frontOnly = false) {
  const group = new T.Group()
  group.name = 'x-pattern-guardrails'
  const { upper, frameLength, entryX, entryWidth } = bunkJoinery(b)
  const stock = X_GUARDRAIL.stock
  function add(name: string, width: number, x: number, z: number, angle = 0) {
    if (width <= 0) return
    const part = createXGuardrailPanel(width, `guard-x-${name}`)
    part.position.set((x - b.width / 2) / 12, base / 12, (z - b.depth / 2) / 12)
    part.rotation.y = angle
    group.add(part)
  }
  if (!frontOnly) add('left', frameLength, upper.x + stock, 0, -Math.PI / 2)
  add('right', frameLength, upper.x + upper.width, 0, -Math.PI / 2)
  if (!frontOnly) add('head', upper.width - 2 * stock, upper.x + stock, 0)
  if (b.ladder !== false) {
    if (!frontOnly) add('foot-left', entryX - upper.x - stock, upper.x + stock, frameLength - stock)
    add(
      'foot-right',
      upper.x + upper.width - stock - entryX - entryWidth,
      entryX + entryWidth,
      frameLength - stock
    )
  }
  return group
}

